import React, { useState, useEffect, useRef } from 'react';
// Assuming React Router for navigation if needed
import { useNavigate } from 'react-router-dom';
// Assuming Clerk for user, replace if using something else
// import { useUser } from '@clerk/clerk-react';
import axios from 'axios'; // Assuming axios for API calls

// Import Lucide Icons
import {
    LayoutDashboard, Settings, Users, Bot, Database, Link, BookOpen, Cpu, Wrench, // Sidebar
    FlaskConical, HelpCircle, FileUp, Zap, Rocket, // Sidebar/Workflow specific
    Search, Bell, ChevronDown, ExternalLink, Mic, // Top Bar / General
    CheckCircle2, XCircle, Clock, Copy, Bookmark, Play, ArrowRight, // Status / Activity / Card Actions
    Target, BrainCircuit, MessageSquare, GitBranch, Share2, // A/B Test, Monitor, Workflow Icons
    DollarSign, BarChart3, Wand2, // Cost Optimizer Icons
    Activity, // Agent Monitor
    Lightbulb, // Knowledge Optimizer
    Server, // Deployment Manager
    ListChecks, // Bulk Action Tool
    Gauge, // Performance Metrics
    Palette, // Theme/Visuals
    Menu, // Services Menu
    Globe, // Region
    LifeBuoy, // Support/Help
    Plus, // Create
    X, // Close
    Eye, // View
    Trash2, // Delete
    PlugZap, // API Type
    Loader2, // Loading
    ShieldAlert // Warning
} from 'lucide-react';

// --- Animation Utility Hook ---
const useOnScreen = (options = { threshold: 0.1 }) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') { setIsVisible(true); return; }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); }
        }, options);
        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef && observer) observer.unobserve(currentRef); };
    }, [options]);
    const hookRef = (node) => { ref.current = node; };
    return [hookRef, isVisible];
};

// --- Styled Clickable Link/Div Component ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
    <a
        href={!disabled ? href : undefined} target={target} rel={target === '_blank' ? 'noopener noreferrer' : rel}
        onClick={!disabled ? onClick : (e) => e.preventDefault()} role="button" tabIndex={disabled ? -1 : 0}
        className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`}
        aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props} >
        {children}
    </a>
);
const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
    <div
        role="button" tabIndex={disabled ? -1 : 0}
        onClick={!disabled ? onClick : undefined}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }}
        className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`}
        aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props} >
        {children}
    </div>
);

// --- Sidebar Component ---
const SidebarNavItem = ({ icon: Icon, text, href, active }) => (
     <a href={href} className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative ${ active ? 'bg-gray-800/60 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50' }`}>
        {active && <div className="absolute left-0 top-1 bottom-1 w-0.5 bg-purple-500 rounded-r-md"></div>}
        <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 ${active ? 'text-purple-400' : 'text-gray-500 group-hover:text-gray-300'}`} strokeWidth={1.5} />
        <span className="truncate">{text}</span>
    </a>
);
const SidebarNavGroup = ({ children }) => ( <div className="mt-3"> <nav className="space-y-0.5 px-2"> {children} </nav> </div> );
const Sidebar = () => {
    // Assuming 'Tools' is the active item for this page context
    const activeItem = 'Tools';
    const navItems = [
        { items: [ { text: 'Dashboard', icon: LayoutDashboard, href: '#dashboard' } ]},
        { items: [ 
            { text: 'Assistants', icon: Bot, href: '#assistants' }, 
            { text: 'Tools', icon: Wrench, href: '#tools' }, 
            { text: 'Knowledge', icon: BookOpen, href: 'https://metabees.org/foundry-docs', target: '_blank' }, 
            { text: 'Datasets', icon: Database, href: '#datasets' }, 
        ]},
        { items: [ { text: 'Deployments', icon: Server, href: '#deployments' }, { text: 'Monitoring', icon: Activity, href: '#monitoring' }, { text: 'Cost Explorer', icon: DollarSign, href: '#billing' }, ]},
        { items: [ { text: 'Playground', icon: FlaskConical, href: '#playground' }, { text: 'Connections', icon: Link, href: '#connections' }, { text: 'Models', icon: Cpu, href: '#models' }, { text: 'Settings', icon: Settings, href: '#settings' }, { text: 'Help', icon: HelpCircle, href: '#help' }, ]},
    ];
    return (
        <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                 <div className="flex items-center">
                    <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/></svg>
                    <span className="text-base font-bold text-white">Echidna Foundry</span>
                </div>
            </div>
            <div className="flex-grow py-2">
                 {navItems.map((group, index) => ( <SidebarNavGroup key={`group-${index}`}> {group.items.map(item => ( <SidebarNavItem key={item.text} icon={item.icon} text={item.text} href={item.href} active={activeItem === item.text} /> ))} </SidebarNavGroup> ))}
            </div>
             <div className="p-2 border-t border-gray-800/50 mt-auto">
                 <a href="#profile" className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white mr-2 group-hover:opacity-90"> PP </span>
                     <span className="truncate font-medium">Peter Parker</span>
                 </a>
             </div>
        </div>
    );
};

// --- Top Bar Component ---
const TopBar = () => {
    return (
        <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
             <div className="flex items-center space-x-3">
                 <ClickableDiv className="p-1.5 -m-1.5 rounded hover:bg-gray-800/50 text-gray-300 hover:text-white" title="Services Menu">
                     <Menu className="w-5 h-5" strokeWidth={1.5}/>
                 </ClickableDiv>
                 {/* Maybe show current page title dynamically */}
                 <span className="text-sm font-medium text-white">Tools</span>
             </div>
             <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="relative hidden md:block">
                      <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/>
                      <input type="search" placeholder="Search tools..." className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" />
                  </div>
                 <ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region">
                     <Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/>
                     <span>us-east-1</span>
                 </ClickableDiv>
                  <ClickableLink href="#help" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                     <LifeBuoy className="w-5 h-5" strokeWidth={1.5}/>
                 </ClickableLink>
                 <ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                     <Bell className="w-5 h-5" strokeWidth={1.5}/>
                 </ClickableDiv>
                  <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                      <span className="hidden md:inline mr-1">Peter P.</span>
                      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white">PP</span>
                      <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
                 </ClickableDiv>
             </div>
        </div>
    );
};

// --- Tool Card Component (For User-Created Tools) ---
const ToolCard = ({ tool, onView, onTest, onDelete, isVisible, delay }) => {
    const toolTypes = { // Define icons for user tool types
        'API': { icon: PlugZap, color: 'text-blue-400' },
        'Function': { icon: Wrench, color: 'text-indigo-400' }, // Example for a custom function tool
        'Knowledge Query': { icon: BookOpen, color: 'text-green-400' } // Example for KB query tool
    };
    const typeInfo = toolTypes[tool.type] || { icon: Wrench, color: 'text-gray-400' };
    const TypeIcon = typeInfo.icon;

    return (
         <div
            key={tool._id}
            className={`bg-gray-950 rounded-lg border border-gray-800/50 p-5 flex flex-col transition-all duration-500 ease-out hover:border-purple-600/50 hover:shadow-xl hover:-translate-y-1 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="flex items-center mb-2">
                <TypeIcon className={`w-4 h-4 mr-2 flex-shrink-0 ${typeInfo.color}`} strokeWidth={1.5} />
                <h2 className="text-base font-semibold text-gray-100 truncate group-hover:text-purple-300 transition-colors" title={tool.toolName}>{tool.toolName}</h2>
            </div>
            <p className="text-xs text-gray-400 mb-4 flex-grow line-clamp-2" title={tool.description}>
                {tool.description || `Type: ${tool.type}`}
            </p>

            <div className="text-gray-500 text-xs mb-4">
                <p>Updated: {new Date(tool.updatedAt).toLocaleDateString()}</p>
            </div>

            {/* Actions Row */}
            <div className="mt-auto grid grid-cols-3 gap-2 pt-3 border-t border-gray-800/50">
                {/* View/Edit Action */}
                <ClickableDiv
                    onClick={() => onView(tool._id)}
                    className="text-[11px] font-medium text-gray-300 border border-gray-700/80 py-1 justify-center hover:bg-gray-800/50 hover:border-gray-700 hover:text-white flex items-center gap-1 group"
                    title="View/Edit Tool"
                >
                   <Eye className="w-3 h-3 group-hover:text-purple-400" strokeWidth={1.5} /> View
                </ClickableDiv>

                 {/* Test Action */}
                <ClickableDiv
                    onClick={() => onTest(tool._id)}
                    className="text-[11px] font-medium text-gray-300 border border-gray-700/80 py-1 justify-center hover:bg-gray-800/50 hover:border-gray-700 hover:text-white flex items-center gap-1 group"
                    title="Test Tool"
                >
                   <FlaskConical className="w-3 h-3 group-hover:text-purple-400" strokeWidth={1.5} /> Test
                </ClickableDiv>

                 {/* Delete Action */}
                 <ClickableDiv
                   onClick={() => onDelete(tool)} // Pass full tool object to delete modal
                   className="text-[11px] font-medium text-red-500/80 border border-red-900/50 bg-red-900/10 py-1 justify-center hover:bg-red-900/30 hover:border-red-700 hover:text-red-400 flex items-center gap-1 group"
                   title="Delete Tool"
                 >
                   <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                </ClickableDiv>
            </div>
        </div>
    );
};


// --- ManageTools Component (Page Content) ---
const ManageTools = () => {
  // const { user } = useUser(); // Use if needed for CID
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');
  // const cid = user?.id;

  const [listRef, isListVisible] = useOnScreen({ threshold: 0.05 });

  // --- Mock Data --- (Replace with API call)
  const mockTools = [
      { _id: 'tool_1a2b', toolName: 'CRM Contact Lookup', description: 'Fetches contact details from Salesforce based on email.', type: 'API', updatedAt: new Date(Date.now() - 86400000 * 2)},
      { _id: 'tool_3c4d', toolName: 'Product Feature KB Query', description: 'Answers questions based on the uploaded Product Features PDF.', type: 'Knowledge Query', updatedAt: new Date(Date.now() - 86400000 * 5)},
      { _id: 'tool_5e6f', toolName: 'Order Status Check (Shopify)', description: 'Connects to Shopify API to retrieve order status.', type: 'API', updatedAt: new Date(Date.now() - 3600000 * 3) },
      { _id: 'tool_7g8h', toolName: 'SQL Database Query Tool', description: 'Executes pre-defined SQL queries against the analytics DB.', type: 'Database', updatedAt: new Date(Date.now() - 86400000 * 10) },
      { _id: 'tool_9i0j', toolName: 'Sentiment Analysis Function', description: 'A custom Python function to analyze text sentiment.', type: 'Function', updatedAt: new Date(Date.now() - 3600000 * 8) },
       { _id: 'tool_1k2l', toolName: 'Calendar Availability Check', description: 'Checks Google Calendar for free slots via API.', type: 'API', updatedAt: new Date(Date.now() - 86400000 * 1)},
  ];

  useEffect(() => {
    // Simulate fetching user's created tools
    const fetchTools = () => {
        setLoading(true);
        setTimeout(() => {
            setTools(mockTools);
            setLoading(false);
        }, 800); // Simulate network delay
    };
    fetchTools();
    // Replace simulation with actual API call using cid if available
    /*
    if (!cid) { setLoading(false); setError("User ID not available."); return; }
     axios.post('YOUR_API_ENDPOINT/mytools', { cid })
        .then(response => setTools(response.data.tools || []))
        .catch(err => setError(err.response?.data?.details || 'Failed to fetch tools'))
        .finally(() => setLoading(false));
    */
  }, []); // Run once on mount

  const handleCreateTool = () => { navigate('/echidna/new-tool'); };
  const handleViewTool = (toolId) => { navigate(`/echidna/tool/${toolId}`); };
  const handleTestTool = (toolId) => { navigate(`/echidna/playground?tool=${toolId}`); };
  const openDeleteModal = (tool) => { setSelectedTool(tool); setShowDeleteModal(true); setConfirmDeleteText(''); setError(null); };

  const handleDeleteTool = async () => {
     if (!selectedTool || confirmDeleteText.toLowerCase() !== 'delete') {
        setError('Please type "delete" to confirm deletion.'); return;
    }
    try {
        setError(null); console.log('Deleting tool:', selectedTool._id);
        // ** Add API call here: await axios.post('...', { toolId: selectedTool._id, cid }); **
        setTools(prevTools => prevTools.filter(t => t._id !== selectedTool._id)); // Update UI optimistically
        setShowDeleteModal(false);
    } catch (err) {
        console.error("Delete Tool Error:", err);
        setError(err.response?.data?.message || 'Failed to delete tool.');
    }
  };

  // Loading State
  if (loading && !tools.length && !error) {
    return (
      <div className="flex-1 bg-black flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full bg-black text-white p-6 lg:p-8 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-950">
        {/* Header */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
            <div>
                 <div className="flex items-center gap-3 mb-1">
                    <Wrench className="w-7 h-7 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
                    <h1 className="text-3xl font-bold tracking-tight text-gray-100">Tools</h1>
                </div>
                <p className="text-base text-gray-400 sm:ml-10">Manage API connections and functions for your AI assistants.</p>
            </div>
            <ClickableDiv onClick={handleCreateTool} className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg shadow-md shadow-purple-600/20 hover:bg-purple-700 gap-2 flex-shrink-0">
                <Plus className="w-5 h-5" strokeWidth={2} /> Create Tool
            </ClickableDiv>
        </header>

      {/* Error Display */}
      {error && !showDeleteModal && ( <div className="mb-8 p-4 border border-red-700/50 bg-gray-950 rounded-lg"><p className="text-sm text-red-400">{error}</p></div> )}

      {/* No Tools State */}
      {!loading && tools.length === 0 && !error ? (
        <div ref={listRef} className={`flex flex-col items-center justify-center text-center bg-gray-950 border border-dashed border-gray-800/50 rounded-lg py-16 px-6 transition-opacity duration-500 ${isListVisible ? 'opacity-100' : 'opacity-0'}`}>
          <Wrench className="w-12 h-12 text-gray-700 mb-4" strokeWidth={1}/>
          <p className="text-gray-500 mb-6">No tools created yet. Tools allow your agents to perform actions.</p>
          <ClickableDiv onClick={handleCreateTool} className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg shadow-lg shadow-purple-600/30 hover:bg-purple-700 transition-colors">
            <Plus className="w-5 h-5" strokeWidth={2} /> Create Your First Tool
          </ClickableDiv>
        </div>
      ) : (
        // Tools Grid
        <div ref={listRef} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {tools.map((tool, index) => (
            <ToolCard key={tool._id} tool={tool} onView={handleViewTool} onTest={handleTestTool} onDelete={openDeleteModal} isVisible={isListVisible} delay={index * 75} />
          ))}
        </div>
      )}

      {/* Delete Tool Modal */}
      {showDeleteModal && selectedTool && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
          <div className="bg-gray-950 border border-gray-800/50 rounded-lg shadow-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-red-400 flex items-center"><ShieldAlert className="w-5 h-5 mr-2" strokeWidth={1.5}/> Delete Tool</h2>
                <div role="button" tabIndex={0} onClick={() => setShowDeleteModal(false)} className="p-1 -m-1 rounded-full text-gray-500 hover:bg-gray-800/50 hover:text-white cursor-pointer"><X className="w-5 h-5" strokeWidth={1.5}/></div>
            </div>
            <p className="text-sm text-gray-400 mb-4">Permanently delete the tool <strong className="text-gray-200">{selectedTool?.toolName}</strong>?</p>
            <div className="mb-6 p-3 bg-red-900/20 border border-red-700/30 rounded-md"><p className="text-xs text-red-400">Warning: This cannot be undone. Agents relying on this tool may cease to function correctly.</p></div>
            {error && showDeleteModal && ( <p className="text-xs text-red-400 mb-4">{error}</p> )}
            <div className="mb-6">
              <label htmlFor="confirm-delete-text" className="block text-xs text-gray-400 mb-1.5">To confirm, type: <code className="text-purple-400 bg-gray-800 px-1 py-0.5 rounded text-xs">delete</code></label>
              <input id="confirm-delete-text" type="text" value={confirmDeleteText} onChange={(e) => setConfirmDeleteText(e.target.value)} className="w-full bg-black/50 border border-gray-700 p-2 text-sm text-white rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600" placeholder="delete"/>
            </div>
            <div className="flex justify-end space-x-3">
               <ClickableDiv onClick={() => setShowDeleteModal(false)} className="px-4 py-1.5 text-sm border border-gray-700 hover:bg-gray-800 transition-colors text-gray-300" >Cancel</ClickableDiv>
               <ClickableDiv onClick={handleDeleteTool} disabled={confirmDeleteText.toLowerCase() !== 'delete'} className={`px-4 py-1.5 text-sm border ${ confirmDeleteText.toLowerCase() === 'delete' ? 'border-red-700/50 bg-red-800/80 hover:bg-red-700/80 text-white' : 'border-gray-700 bg-gray-800 text-gray-500'}`} > <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5}/> Delete Permanently </ClickableDiv>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// --- Main Application Component (Renamed to Echtools) ---
// This renders the Sidebar, TopBar, and the ManageTools page content
function Echtools() {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
        <Sidebar />
        {/* Main Content Area - Renders ManageTools */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
             <TopBar />
             {/* The ManageTools component will handle its own internal scrolling */}
             <div className="blur-sm pointer-events-none">
               <ManageTools />
             </div>
             {/* Coming Soon Overlay */}
             <div className="absolute inset-0 flex items-center justify-center z-50">
               <div className="bg-black/90 backdrop-blur-md border border-purple-500/30 rounded-xl p-8 max-w-md w-full mx-4 text-center">
                 <Rocket className="w-12 h-12 text-purple-500 mx-auto mb-4" strokeWidth={1.5} />
                 <h2 className="text-2xl font-bold text-white mb-2">Coming Soon</h2>
                 <p className="text-gray-400 mb-6">We're working on something exciting! This feature will be available soon.</p>
                 <div className="flex justify-center">
                   <ClickableDiv 
                     onClick={() => navigate('/echidna/efoundry/dashboard')}
                     className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                   >
                     <ArrowRight className="w-4 h-4 rotate-180" strokeWidth={1.5} /> Go Back to Dashboard
                   </ClickableDiv>
                 </div>
               </div>
             </div>
         </div>
        <style jsx global>{`
            /* Minimal scrollbar styles */
             .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) transparent; }
             .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
             .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; }
             .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }
             body { background-color: black; } /* Ensure body bg matches */
              @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
             .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
             @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
             .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
        `}</style>
    </div>
  );
}

// Renamed export
export default Echtools;